import React, { useEffect, useMemo, useRef, useState } from "react";

type Breakpoint = { sm?: number; md?: number; lg?: number };

export type CarouselProps<T> = {
  items: T[];
  renderItem: (item: T, index: number) => React.ReactNode;
  perView?: number | Breakpoint;   // quantos cards por “página”
  gap?: number;                    // px entre slides
  autoplayMs?: number;             // 0 = desliga
  className?: string;
  ariaLabel?: string;
  maxWidth?: number | string;      // ex: 1200 ou "100%"
  aspectRatio?: string;            // ex: "4 / 3", "16 / 9"
  slideHeights?: Breakpoint;       // altura por breakpoint (se não usar aspectRatio)
  itemClassName?: string;          // classe aplicada ao wrapper do item
};

function resolvePerView(perView?: number | Breakpoint) {
  if (!perView) return 3;
  if (typeof perView === "number") return perView;
  const w = window.innerWidth;
  if (w < 640) return perView.sm ?? 1;
  if (w < 1024) return perView.md ?? perView.sm ?? 1;
  return perView.lg ?? perView.md ?? perView.sm ?? 3;
}
function resolveHeight(slideHeights?: Breakpoint) {
  if (!slideHeights) return undefined;
  const w = window.innerWidth;
  if (w < 640) return slideHeights.sm;
  if (w < 1024) return slideHeights.md ?? slideHeights.sm;
  return slideHeights.lg ?? slideHeights.md ?? slideHeights.sm;
}

export default function Carousel<T>({
  items,
  renderItem,
  perView = { sm: 1, md: 2, lg: 3 },
  gap = 16,
  autoplayMs = 0,
  className = "",
  ariaLabel = "Carrossel",
  maxWidth = "100%",
  aspectRatio,
  slideHeights,
  itemClassName = "",
}: CarouselProps<T>) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(0);
  const [pv, setPv] = useState<number>(typeof perView === "number" ? perView : 3);
  const [slideH, setSlideH] = useState<number | undefined>(resolveHeight(slideHeights));

  // recalcula perView/height em resize
  useEffect(() => {
    const apply = () => {
      setPv(resolvePerView(perView));
      setSlideH(resolveHeight(slideHeights));
    };
    apply();
    window.addEventListener("resize", apply);
    return () => window.removeEventListener("resize", apply);
  }, [perView, slideHeights]);

  const pageCount = useMemo(() => Math.max(1, Math.ceil(items.length / pv)), [items.length, pv]);

  // atualiza página ao rolar
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const onScroll = () => {
      const width = el.clientWidth;
      const p = Math.round(el.scrollLeft / (width + 0.0001));
      setPage(Math.max(0, Math.min(pageCount - 1, p)));
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [pageCount]);

  // autoplay
  useEffect(() => {
    if (!autoplayMs) return;
    const id = setInterval(() => goTo(page + 1), autoplayMs);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, autoplayMs, pv, items.length]);

  const goTo = (p: number) => {
    const el = trackRef.current;
    if (!el) return;
    const width = el.clientWidth;
    const target = Math.max(0, Math.min(pageCount - 1, p));
    el.scrollTo({ left: target * width, behavior: "smooth" });
  };
  const prev = () => goTo(page - 1);
  const next = () => goTo(page + 1);

  // arrasto mouse/touch
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    let isDown = false, startX = 0, scrollLeft = 0;

    const onDown = (e: MouseEvent | TouchEvent) => {
      isDown = true;
      startX = ("touches" in e ? e.touches[0].pageX : (e as MouseEvent).pageX) - el.offsetLeft;
      scrollLeft = el.scrollLeft;
    };
    const onMove = (e: MouseEvent | TouchEvent) => {
      if (!isDown) return;
      const x = ("touches" in e ? e.touches[0].pageX : (e as MouseEvent).pageX) - el.offsetLeft;
      el.scrollLeft = scrollLeft - (x - startX);
    };
    const onUp = () => { isDown = false; };

    el.addEventListener("mousedown", onDown);
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseup", onUp);
    el.addEventListener("mouseleave", onUp);
    el.addEventListener("touchstart", onDown, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: true });
    el.addEventListener("touchend", onUp);

    return () => {
      el.removeEventListener("mousedown", onDown);
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseup", onUp);
      el.removeEventListener("mouseleave", onUp);
      el.removeEventListener("touchstart", onDown);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onUp);
    };
  }, []);

  const slideBasis = `${100 / pv}%`;
  const rootStyle: React.CSSProperties = {
    maxWidth: typeof maxWidth === "number" ? `${maxWidth}px` : maxWidth,
    marginInline: "auto",
  };
  const itemboxStyle: React.CSSProperties =
    aspectRatio
      ? { aspectRatio, width: "100%", borderRadius: 16, overflow: "hidden" }
      : slideH
      ? { height: slideH, width: "100%", borderRadius: 16, overflow: "hidden" }
      : { width: "100%" };

  return (
    <div className={`carousel ${className}`} style={rootStyle}>
      <button className="carousel-arrow left" onClick={prev} aria-label="Anterior">‹</button>

      <div
        className="carousel-track"
        ref={trackRef}
        style={{ gap: `${gap}px` }}
        role="region"
        aria-roledescription="carousel"
        aria-label={ariaLabel}
      >
        {[...Array(pageCount)].map((_, pIdx) => {
          const start = pIdx * pv;
          const subset = items.slice(start, start + pv);
          return (
            <div className="carousel-page" key={`p-${pIdx}`}>
              {subset.map((item, i) => (
                <div className="carousel-slide" style={{ flexBasis: slideBasis }} key={`s-${start + i}`}>
                  <div className={`carousel-itembox ${itemClassName}`} style={itemboxStyle}>
                    {renderItem(item, start + i)}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      <button className="carousel-arrow right" onClick={next} aria-label="Próximo">›</button>

      <div className="carousel-dots" role="tablist" aria-label="Navegação do carrossel">
        {Array.from({ length: pageCount }, (_, i) => (
          <button
            key={i}
            className={`carousel-dot ${i === page ? "active" : ""}`}
            onClick={() => goTo(i)}
            role="tab"
            aria-selected={i === page}
            aria-label={`Ir para página ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}
